// 📁 src/App.js

import './index.css';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// 🌐 Public Pages
import Homepage from './Homepage';
import Products from './Products';
import RequestDemo from './RequestDemo';
import CustomerSignupPortal from './components/tenant/CustomerSignupPortal';
import OTPVerification from './components/tenant/OTPVerification';
import PaymentPage from './components/Payment/PaymentPage';
import CartPage from './components/Cart/CartPage';
import ContactPage from './ContactPage';
import SignInPage from './components/auth/SignInPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import ResetPasswordConfirmPage from './components/auth/ResetPasswordConfirmPage';

// 🛠 Admin Pages
import DashboardRouter from './admin/DashboardRouter';
import TenantDashboard from './admin/TenantDashboard';
import AdminPanel from './components/admin/AdminPanel';
import TenantMatrixPage from './pages/admin/TenantMatrixPage';
import ModuleAnalyticsPage from './pages/admin/ModuleAnalyticsPage';

// 🧩 Layouts
import PublicLayout from './layouts/PublicLayout';
import AdminLayout from './layouts/AdminLayout';
import AccountLayout from './layouts/AccountLayout';

// 🔐 Auth Hook
import useAuth from './hooks/useAuth';

// 🧑‍💼 My Account Pages
import PersonalSettings from './components/account/PersonalSettings';
import BillingPage from './components/account/BillingPage';
import SubscriptionPage from './components/account/SubscriptionPage';
import TeamPage from './components/account/TeamPage';
import PreferencesPage from './components/account/PreferencesPage';
import AuditLogPage from './components/account/AuditLogPage';
import SupportPage from './components/account/SupportPage';

// 🧩 Tenant Software Interface
import TenantSoftwarePanel from './components/account/TenantSoftwarePanel';
import ATSModule from './components/account/modules/ATSModule';
import PeopleSyncModule from './components/account/modules/PeopleSyncModule';
import ScoutModule from './components/account/modules/ScoutModule';
import AuditReadyModule from './components/account/modules/AuditReadyModule';
import PerformEdgeModule from './components/account/modules/PerformEdgeModule';

// 🧾 Invoice Preview Page
import InvoicePreviewPage from './components/invoice/InvoicePreviewPage.jsx';

// 🔐 Global Registry Initialization
if (!window.accountRegistry) {
  window.accountRegistry = {
    companyId: 'TALENTRAXION001',
    moduleAccess: {
      ATS: false,
      PeopleSync: false,
      Scout: false,
      AuditReady: false,
      PerformEdge: false
    },
    paymentStatus: {
      ATS: 'unpaid',
      PeopleSync: 'unpaid',
      Scout: 'unpaid',
      AuditReady: 'unpaid',
      PerformEdge: 'unpaid'
    }
  };
}

export default function App() {
  const { user } = useAuth();

  // 🔹 Read registration mode from localStorage (set via CustomerInfoPage toggle)
  const registrationMode = localStorage.getItem('registrationMode') || 'public';

  return (
    <Router>
      <Routes>
        {/* 🌐 Public Website */}
        <Route path="/" element={<PublicLayout />}>
          <Route index element={<Homepage />} />
          <Route path="products" element={<Products />} />
          <Route path="demo" element={<RequestDemo />} />

          {/* 🔹 Registration Routes */}
          <Route path="register" element={<CustomerSignupPortal mode="public" />} />
          <Route path="internal-register" element={<CustomerSignupPortal mode="private" />} />

          <Route path="otp" element={<OTPVerification />} />
          <Route path="payment" element={<PaymentPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="signin" element={<SignInPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="reset-password-confirm" element={<ResetPasswordConfirmPage />} />
        </Route>

        {/* ✅ Invoice Preview (Standalone Route) */}
        <Route path="/invoice-preview" element={<InvoicePreviewPage />} />

        {/* 🧑‍💼 My Account Routes */}
        <Route path="/my-account" element={<AccountLayout />}>
          <Route path="account-settings" element={<PersonalSettings />} />
          <Route path="software">
            <Route index element={<TenantSoftwarePanel />} />
            <Route path="ats" element={<ATSModule />} />
            <Route path="peoplesync" element={<PeopleSyncModule />} />
            <Route path="scout" element={<ScoutModule />} />
            <Route path="auditready" element={<AuditReadyModule />} />
            <Route path="performedge" element={<PerformEdgeModule />} />
          </Route>
          <Route path="billing" element={<BillingPage />} />
          <Route path="subscription" element={<SubscriptionPage />} />
          <Route path="team" element={<TeamPage />} />
          <Route path="preferences" element={<PreferencesPage />} />
          <Route path="audit" element={<AuditLogPage />} />
          <Route path="support" element={<SupportPage />} />
        </Route>

        {/* 🛠 Admin Software */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<DashboardRouter />} />
          <Route path="panel" element={<AdminPanel />} />
          <Route path="matrix" element={<TenantMatrixPage />} />
          <Route path="analytics" element={<ModuleAnalyticsPage />} />
          <Route
            path="tenant/:tenantId/dashboard"
            element={
              ['superadmin', 'tenantAdmin'].includes(user?.role) ? (
                <TenantDashboard />
              ) : (
                <Navigate to="/admin" replace />
              )
            }
          />
        </Route>
      </Routes>
    </Router>
  );
}
